import { cn } from '@/lib/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ExportCategory, ExportCategoryId } from './use-export-categories'

interface ExportCategoryNavProps {
  categories: ExportCategory[]
  selected: ExportCategoryId
  onSelect(id: ExportCategoryId): void
}

export const ExportCategoryNav: React.FC<ExportCategoryNavProps> = ({
  categories,
  selected,
  onSelect,
}) => {
  const translations = useTranslate({
    categories: 'exports.page.categories',
  })

  return (
    <>
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] xl:hidden">
        {categories.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            aria-pressed={id === selected}
            className={cn(
              'inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors',
              id === selected
                ? 'border-stone-900 bg-stone-900 text-white'
                : 'border-stone-200 bg-white text-stone-700 hover:border-stone-300'
            )}
            onClick={() => onSelect(id)}
          >
            <Icon size={16} strokeWidth={1.5} />
            {label}
          </button>
        ))}
      </div>

      <nav className="hidden overflow-hidden rounded-2xl bg-white shadow-card xl:sticky xl:top-4 xl:block">
        <p className="border-b border-stone-200 px-5 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-stone-500">
          {translations.categories}
        </p>

        <ul className="py-2">
          {categories.map(({ id, label, icon: Icon }) => (
            <li key={id}>
              <button
                type="button"
                aria-current={id === selected ? 'page' : undefined}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-3 border-l-2 px-5 py-2.5 text-left text-sm transition-colors',
                  id === selected
                    ? 'border-primary bg-stone-100 font-semibold text-stone-900'
                    : 'border-transparent font-medium text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                )}
                onClick={() => onSelect(id)}
              >
                <Icon
                  size={18}
                  strokeWidth={1.5}
                  className={cn(
                    id === selected ? 'text-stone-900' : 'text-stone-400'
                  )}
                />
                {label}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </>
  )
}
